import React, { useEffect, useState } from "react";
import "./style.css";
import {
  Accordion,
  Button,
  Card,
  Col,
  Form,
  Row,
  Container,
  InputGroup,
  Alert,
  Modal,
  ListGroup,
  Stack,
} from "react-bootstrap";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import * as Icon from "react-bootstrap-icons";
import { PolicyType } from "../../models/PolicyType";
import PolicyTypeService from "../../services/policyType.service";
import QuestionaryService from "../../services/audit.service";
import { Section } from "../../models/Section";
import SectionService from "../../services/section.service";
import ModuleService from "../../services/auditModule.service";
import SubsectionService from "../../services/subsection.service";
import { Subsection } from "../../models/Subsection";
import { AuditQuestionsManager } from "./AuditQuestionsManager";
import { AuditModule } from "../../models/AuditModule";
import DeleteButton from "../../Helpers/deleteButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faList,
  faArrowLeft,
  faBan,
  faAdd,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

export const AuditSectionsManager = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [policyTypes, setPolicyTypes] = useState<PolicyType[]>([]);
  const [modules, setModules] = useState<AuditModule[]>([]);
  const [policyTypeId, setPolicyTypeId] = useState<number>(-1);
  const [selectedSubSection, setSelectedSubsection] = useState<Subsection>();
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [isErrorMessage, setIsErrorMessage] = useState<boolean>(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState("");
  const [cloneError, setCloneError] = useState("");
  const [droppableId, setDroppableId] = useState("modules-" + Math.random());

  // Nuevos estados para controlar acordeones abiertos
  const [expandedModuleIds, setExpandedModuleIds] = useState<number[]>([]);
  const [expandedSectionIds, setExpandedSectionIds] = useState<number[]>([]);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [subsectionToMove, setSubsectionToMove] = useState<{
    subsection: Subsection;
    fromSectionId: number;
    fromModuleId: number;
  } | null>(null);

  const recalculatePositions = <T extends { position: number }>(
    items: T[]
  ): T[] => {
    return items
      .sort((a, b) => a.position - b.position)
      .map((item, index) => ({
        ...item,
        position: index + 1,
      }));
  };

  async function onPolicyTypeChange(policyTypeId: number) {
    if (!isNaN(policyTypeId)) {
      try {
        let modules = await QuestionaryService.getModules(policyTypeId);
        modules = modules
          .sort((a, b) => a.position - b.position)
          .map((mod) => ({
            ...mod,
            sections: (mod.sections || [])
              .sort((a, b) => a.position - b.position)
              .map((sec) => ({
                ...sec,
                subsections: (sec.subsections || []).sort(
                  (a, b) => a.position - b.position
                ),
              })),
          }));

        setModules(modules);
        setPolicyTypeId(policyTypeId);

        // Resetear acordeones abiertos al cambiar política
        setExpandedModuleIds([]);
        setExpandedSectionIds([]);
      } catch (error) {
        setPopupMessage("Error cargando módulos");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    } else {
      setModules([]);
      setPolicyTypeId(-1);
    }
  }

  function handleShowDetails(selectedSubSection: Subsection) {
    setSelectedSubsection(selectedSubSection);
    setShowDetails(true);
  }

  async function onModuleInsert() {
    const newPosition =
      modules.length > 0 ? Math.max(...modules.map((m) => m.position)) + 1 : 1;

    let module: AuditModule = {
      id: 0,
      name: "Nuevo Módulo",
      policyTypeId: policyTypeId,
      position: newPosition,
    };

    try {
      const response = await ModuleService.insertAuditModule(module);
      if (response.ok) {
        const result = await response.json();
        module.id = result;
        setModules([...modules, module]);
      } else {
        throw new Error("Error inserting module");
      }
    } catch (error) {
      setPopupMessage("Error al insertar módulo");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
  }

  async function onSectionInsert(module: AuditModule) {
    const sections = module.sections || [];
    const newPosition =
      sections.length > 0
        ? Math.max(...sections.map((s) => s.position)) + 1
        : 1;

    let section: Section = {
      id: 0,
      name: "Nueva sección",
      auditModuleId: module.id,
      position: newPosition,
    };

    try {
      const response = await SectionService.insertSection(section);
      if (response.ok) {
        const result = await response.json();
        section.id = result;

        const updatedModules = modules.map((m) =>
          m.id === module.id
            ? {
                ...m,
                sections: [...(m.sections || []), section],
              }
            : m
        );

        setModules(updatedModules);
      } else {
        throw new Error("Error inserting section");
      }
    } catch (error) {
      setPopupMessage("Error al insertar sección");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
  }

  async function onSectionDelete(section: Section, moduleId: number) {
    try {
      const response = await SectionService.deleteSection(section);
      if (response.ok) {
        const myNewList = [...modules];
        const currentModule = myNewList.find((a) => a.id == moduleId);

        if (currentModule) {
          const newSections = (currentModule.sections || []).filter(
            (a) => a.id != section.id
          );

          const updatedSections = recalculatePositions(newSections);

          currentModule.sections = updatedSections;
          setModules(myNewList);

          await SectionService.updatePositions(
            updatedSections.map((s) => ({
              sectionId: s.id,
              newPosition: s.position,
            }))
          );
        }

        setPopupMessage("Se eliminó la sección exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);
      } else {
        setPopupMessage(
          "No se puede eliminar el registro porque hay información relacionada"
        );
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    } catch (error) {
      setPopupMessage("Error al eliminar el registro");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
    window.scrollTo(0, 0);
  }

  async function onModuleDelete(module: AuditModule) {
    try {
      const response = await ModuleService.deleteAuditModule(module);
      if (response.ok) {
        const newModules = modules.filter(
          (currentModule) => currentModule.id !== module.id
        );

        const updatedModules = recalculatePositions(newModules);
        setModules(updatedModules);
        await ModuleService.updatePositions(
          updatedModules.map((m) => ({
            moduleId: m.id,
            newPosition: m.position,
          }))
        );

        setPopupMessage("Se eliminó el módulo exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);
      } else {
        setPopupMessage(
          "No se puede eliminar el registro porque hay información relacionada"
        );
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    } catch (error) {
      setPopupMessage("Error al eliminar el registro");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
    window.scrollTo(0, 0);
  }

  async function onSubsectionDelete(
    subsection: Subsection,
    sectionId: number,
    moduleId: number
  ) {
    try {
      const response = await SubsectionService.deleteSubsection(subsection);
      if (response.ok) {
        const myNewList = [...modules];
        const currentModule = myNewList.find((a) => a.id == moduleId);

        if (currentModule) {
          const currentSection = (currentModule.sections || []).find(
            (a) => a.id == sectionId
          );

          if (currentSection) {
            const newSubsections = (currentSection.subsections || []).filter(
              (a) => a.id != subsection.id
            );
            currentSection.subsections = newSubsections;
            const updatedSubsections = recalculatePositions(newSubsections);
            currentSection.subsections = updatedSubsections;
            await SubsectionService.updatePositions(
              updatedSubsections.map((s) => ({
                subsectionId: s.id,
                newPosition: s.position,
              }))
            );
          }
          setModules(myNewList);
        }

        setPopupMessage("Se eliminó la subsección exitosamente");
        setPopupTitle("Registro eliminado");
        setIsErrorMessage(false);
        setShowPopup(true);
      } else {
        setPopupMessage(
          "No se puede eliminar el registro porque hay información relacionada"
        );
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    } catch (error) {
      setPopupMessage("Error al eliminar el registro");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
    window.scrollTo(0, 0);
  }

  function onSectionUpdate(
    sectionName: string,
    sectionId: number,
    moduleId: number
  ) {
    const myNewList = [...modules];
    const currentModule = myNewList.find((a) => a.id == moduleId);

    if (currentModule) {
      const sectionIndex = (currentModule.sections || []).findIndex(
        (s) => s.id === sectionId
      );
      if (sectionIndex !== -1) {
        currentModule.sections = currentModule.sections || [];
        currentModule.sections[sectionIndex].name = sectionName;
        setModules(myNewList);
      }
    }
  }

  function onModuleUpdate(moduleName: string, moduleId: number) {
    const myNewList = [...modules];
    const currentModule = myNewList.find((a) => a.id == moduleId);

    if (currentModule) {
      currentModule.name = moduleName;
      setModules(myNewList);
    }
  }

  function onSubsectionUpdate(
    subsectionName: string,
    subsectionId: number,
    sectionId: number,
    moduleId: number
  ) {
    const myNewList = [...modules];
    const currentModule = myNewList.find((a) => a.id == moduleId);

    if (currentModule) {
      currentModule.sections = currentModule.sections || [];
      const sectionIndex = (currentModule.sections || []).findIndex(
        (s) => s.id === sectionId
      );
      if (sectionIndex !== -1) {
        const section = currentModule.sections[sectionIndex];
        section.subsections = section.subsections || [];
        const subsectionIndex = (section.subsections || []).findIndex(
          (sub) => sub.id === subsectionId
        );
        if (subsectionIndex !== -1) {
          section.subsections[subsectionIndex].name = subsectionName;
          setModules(myNewList);
        }
      }
    }
  }

  async function onSubSectionInsert(section: Section, moduleId: number) {
    const subsections = section.subsections || [];
    const newPosition =
      subsections.length > 0
        ? Math.max(...subsections.map((ss) => ss.position)) + 1
        : 1;

    let subsection: Subsection = {
      id: 0,
      name: "Nueva sub sección",
      sectionId: section.id,
      position: newPosition,
    };

    try {
      const response = await SubsectionService.insertSubsection(subsection);
      if (response.ok) {
        const result = await response.json();
        subsection.id = result;

        const updatedModules = modules.map((m) => {
          if (m.id === moduleId) {
            const updatedSections = (m.sections || []).map((sec) => {
              if (sec.id === section.id) {
                return {
                  ...sec,
                  subsections: [...(sec.subsections || []), subsection],
                };
              }
              return sec;
            });
            return { ...m, sections: updatedSections };
          }
          return m;
        });

        setModules(updatedModules);
      } else {
        throw new Error("Error inserting subsection");
      }
    } catch (error) {
      setPopupMessage("Error al insertar subsección");
      setPopupTitle("Error");
      setIsErrorMessage(true);
      setShowPopup(true);
    }
  }

  async function onSectionChange(currentSection: Section) {
    try {
      await SectionService.updateSection(currentSection);
    } catch (error) {
      console.error("Error updating section:", error);
    }
  }

  async function onModuleChange(currentModule: AuditModule) {
    try {
      await ModuleService.updateAuditModule(currentModule);
    } catch (error) {
      console.error("Error updating module:", error);
    }
  }

  async function onSubsectionChange(currentSubsection: Subsection) {
    try {
      await SubsectionService.updateSubsection(currentSubsection);
    } catch (error) {
      console.error("Error updating subsection:", error);
    }
  }

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    if (result.type === "MODULE") {
      const reorderedModules = [...modules];
      const [movedModule] = reorderedModules.splice(result.source.index, 1);
      reorderedModules.splice(result.destination.index, 0, movedModule);

      const updatedModules = reorderedModules.map((mod, index) => ({
        ...mod,
        position: index + 1,
      }));

      setModules(updatedModules);

      try {
        await ModuleService.updatePositions(
          updatedModules.map((m) => ({
            moduleId: m.id,
            newPosition: m.position,
          }))
        );
      } catch (error) {
        setPopupMessage("Error al actualizar el orden de los módulos");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
        setModules(modules);
      }
    }

    if (result.type === "SECTION") {
      const moduleId = parseInt(result.destination.droppableId);
      const updatedModules = [...modules];
      const moduleIndex = updatedModules.findIndex((m) => m.id === moduleId);

      if (moduleIndex === -1) return;

      const sections = [...(updatedModules[moduleIndex].sections || [])];
      const [movedSection] = sections.splice(result.source.index, 1);
      sections.splice(result.destination.index, 0, movedSection);

      const updatedSections = sections.map((sec, index) => ({
        ...sec,
        position: index + 1,
      }));

      updatedModules[moduleIndex] = {
        ...updatedModules[moduleIndex],
        sections: updatedSections,
      };

      setModules(updatedModules);

      try {
        await SectionService.updatePositions(
          updatedSections.map((s) => ({
            sectionId: s.id,
            newPosition: s.position,
          }))
        );
      } catch (error) {
        setPopupMessage("Error al actualizar el orden de las secciones");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
        setModules(modules);
      }
    }

    if (result.type === "SUBSECTION") {
      const sectionId = parseInt(result.destination.droppableId);
      const updatedModules = [...modules];

      let sectionFound = false;
      for (let i = 0; i < updatedModules.length && !sectionFound; i++) {
        const sections = updatedModules[i].sections || [];
        for (let j = 0; j < sections.length && !sectionFound; j++) {
          if (sections[j].id === sectionId) {
            const subsections = [...(sections[j].subsections || [])];
            const [movedSubsection] = subsections.splice(
              result.source.index,
              1
            );
            subsections.splice(result.destination.index, 0, movedSubsection);

            const updatedSubsections = subsections.map((sub, index) => ({
              ...sub,
              position: index + 1,
            }));

            updatedModules[i] = {
              ...updatedModules[i],
              sections: updatedModules[i].sections?.map((sec) =>
                sec.id === sectionId
                  ? {
                      ...sec,
                      subsections: updatedSubsections,
                    }
                  : sec
              ),
            };

            setModules(updatedModules);

            try {
              await SubsectionService.updatePositions(
                updatedSubsections.map((sub) => ({
                  subsectionId: sub.id,
                  newPosition: sub.position,
                }))
              );
            } catch (error) {
              setPopupMessage(
                "Error al actualizar el orden de las subsecciones"
              );
              setPopupTitle("Error");
              setIsErrorMessage(true);
              setShowPopup(true);
              setModules(modules);
            }

            sectionFound = true;
          }
        }
      }
    }
  };

  const handleCloneClick = () => {
    if (policyTypeId <= 0) return;
    setShowCloneModal(true);
    setCloneError("");
    setNewPolicyName("");
  };

  const handleCloseCloneModal = () => {
    setShowCloneModal(false);
    setNewPolicyName("");
    setCloneError("");
  };

  const handleCloneSave = async () => {
    if (!newPolicyName.trim()) {
      setCloneError("El nombre de la nueva política no puede estar vacío.");
      return;
    }

    try {
      const request = {
        auditTypeIdToClone: policyTypeId,
        newAuditTypeName: newPolicyName,
      };

      await PolicyTypeService.clone(request);

      const updatedPolicyTypes = await PolicyTypeService.getPoliciTypes();
      setPolicyTypes(updatedPolicyTypes);

      handleCloseCloneModal();
      setPopupMessage("Política clonada exitosamente");
      setPopupTitle("Clonación exitosa");
      setIsErrorMessage(false);
      setShowPopup(true);
    } catch (error: any) {
      setCloneError(`Error al clonar: ${error.message}`);
    }
  };

  const handleStartMove = (
    subsection: Subsection,
    fromSectionId: number,
    fromModuleId: number
  ) => {
    setSubsectionToMove({ subsection, fromSectionId, fromModuleId });
    setShowMoveModal(true);
  };

  const handleCloseMoveModal = () => {
    setShowMoveModal(false);
    setSubsectionToMove(null);
  };

  const handleConfirmMove = async (newSectionId: number) => {
    if (!subsectionToMove) return;
    const { subsection } = subsectionToMove;
    await SubsectionService.moveSubsection(subsection.id, newSectionId);

    // Recargar los módulos para asegurar la consistencia de los datos
    await onPolicyTypeChange(policyTypeId);

    handleCloseMoveModal();
    setPopupMessage("Subsección movida exitosamente.");
    setPopupTitle("Éxito");
    setIsErrorMessage(false);
    setShowPopup(true);
  };

  useEffect(() => {
    async function loadPolicyTypes() {
      try {
        const data = await PolicyTypeService.getPoliciTypes();

        // Orden Alfabetico
        const sortedData = data.sort((a, b) =>
          a.policyDesc.localeCompare(b.policyDesc)
        );
        setPolicyTypes(sortedData.filter((pt) => !pt.isArchived));
      } catch (error) {
        setPopupMessage("Error cargando tipos de política");
        setPopupTitle("Error");
        setIsErrorMessage(true);
        setShowPopup(true);
      }
    }

    loadPolicyTypes();
  }, []);

  useEffect(() => {
    setDroppableId("modules-" + modules.length);
  }, [modules.length]);

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        setShowPopup(false);
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [showPopup]);

  return (
    <div>
      <div className="notification-container">
        {showPopup && (
          <Alert
            variant={isErrorMessage ? "danger" : "success"}
            onClose={() => setShowPopup(false)}
            className="notification-alert"
            dismissible
          >
            <Alert.Heading>{popupTitle}</Alert.Heading>
            <p>{popupMessage}</p>
          </Alert>
        )}
      </div>

      <Container className="pt-3">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Card>
            <Card.Body>
              <Card.Title>Administrador de Auditorías</Card.Title>
              <hr />
              <Container>
                <Row>
                  <Col xs md={4}>
                    <Form.Select
                      aria-label=""
                      onChange={(e) =>
                        onPolicyTypeChange(parseInt(e.target.value))
                      }
                    >
                      <option>Selecciona un tipo de poliza</option>
                      {policyTypes.map((policyType) => (
                        <option key={policyType.id} value={policyType.id}>
                          {policyType.policyDesc}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="pt-3">
                  <Col>
                    {modules.length > 0 && (
                      <Droppable droppableId={droppableId} type="MODULE">
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                          >
                            <Accordion
                              activeKey={expandedModuleIds.map(String)}
                              onSelect={(eventKeys) => {
                                // Manejar la apertura/cierre de módulos
                                let newKeys: number[];
                                if (Array.isArray(eventKeys)) {
                                  newKeys = eventKeys.map(Number);
                                } else if (eventKeys) {
                                  newKeys = [Number(eventKeys)];
                                } else {
                                  newKeys = [];
                                }
                                setExpandedModuleIds(newKeys);
                              }}
                              alwaysOpen
                            >
                              {modules.map((module, moduleIndex) => (
                                <Draggable
                                  key={module.id}
                                  draggableId={`module-${module.id}`}
                                  index={moduleIndex}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        transition: "all 0.3s ease",
                                        backgroundColor: snapshot.isDragging
                                          ? "#f8f9fa"
                                          : "white",
                                        marginBottom: "10px",
                                      }}
                                    >
                                      <Accordion.Item
                                        eventKey={module.id.toString()}
                                      >
                                        <Accordion.Header>
                                          <div className="d-flex align-items-center w-100">
                                            <div
                                              {...provided.dragHandleProps}
                                              className="drag-handle me-2"
                                              style={{
                                                cursor: snapshot.isDragging
                                                  ? "grabbing"
                                                  : "grab",
                                              }}
                                            >
                                              <Icon.GripVertical className="text-muted" />
                                            </div>
                                            <span className="badge bg-primary me-2">
                                              {module.position}
                                            </span>
                                            <InputGroup
                                              className="mb-0"
                                              size="sm"
                                            >
                                              <Form.Control
                                                type="text"
                                                value={module.name}
                                                onChange={(e) => {
                                                  onModuleUpdate(
                                                    e.target.value,
                                                    module.id
                                                  );
                                                }}
                                                onBlur={(e) => {
                                                  onModuleChange(module);
                                                }}
                                              />
                                              <DeleteButton
                                                onClick={() =>
                                                  onModuleDelete(module)
                                                }
                                              />
                                            </InputGroup>
                                          </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                          <Row>
                                            <Col
                                              xs
                                              md={{
                                                span: 10,
                                                offset: 1,
                                              }}
                                            >
                                              {(module.sections || []).length >
                                                0 && (
                                                <Droppable
                                                  droppableId={module.id.toString()}
                                                  type="SECTION"
                                                >
                                                  {(provided) => (
                                                    <div
                                                      {...provided.droppableProps}
                                                      ref={provided.innerRef}
                                                    >
                                                      <Accordion
                                                        activeKey={expandedSectionIds.map(
                                                          String
                                                        )}
                                                        onSelect={(
                                                          eventKeys
                                                        ) => {
                                                          // Manejar la apertura/cierre de secciones
                                                          let newKeys: number[];
                                                          if (
                                                            Array.isArray(
                                                              eventKeys
                                                            )
                                                          ) {
                                                            newKeys =
                                                              eventKeys.map(
                                                                Number
                                                              );
                                                          } else if (
                                                            eventKeys
                                                          ) {
                                                            newKeys = [
                                                              Number(eventKeys),
                                                            ];
                                                          } else {
                                                            newKeys = [];
                                                          }
                                                          setExpandedSectionIds(
                                                            newKeys
                                                          );
                                                        }}
                                                        alwaysOpen
                                                      >
                                                        {(
                                                          module.sections || []
                                                        ).map(
                                                          (
                                                            section,
                                                            sectionIndex
                                                          ) => (
                                                            <Draggable
                                                              key={section.id}
                                                              draggableId={`section-${section.id}`}
                                                              index={
                                                                sectionIndex
                                                              }
                                                            >
                                                              {(
                                                                provided,
                                                                snapshot
                                                              ) => (
                                                                <div
                                                                  ref={
                                                                    provided.innerRef
                                                                  }
                                                                  {...provided.draggableProps}
                                                                  style={{
                                                                    ...provided
                                                                      .draggableProps
                                                                      .style,
                                                                    transition:
                                                                      "all 0.3s ease",
                                                                    backgroundColor:
                                                                      snapshot.isDragging
                                                                        ? "#f8f9fa"
                                                                        : "white",
                                                                    marginBottom:
                                                                      "10px",
                                                                  }}
                                                                >
                                                                  <Accordion.Item
                                                                    eventKey={section.id.toString()}
                                                                  >
                                                                    <Accordion.Header>
                                                                      <div className="d-flex align-items-center w-100">
                                                                        <div
                                                                          {...provided.dragHandleProps}
                                                                          className="drag-handle me-2"
                                                                          style={{
                                                                            cursor:
                                                                              snapshot.isDragging
                                                                                ? "grabbing"
                                                                                : "grab",
                                                                          }}
                                                                        >
                                                                          <Icon.GripVertical className="text-muted" />
                                                                        </div>
                                                                        <span className="badge bg-secondary me-2">
                                                                          {
                                                                            section.position
                                                                          }
                                                                        </span>
                                                                        <InputGroup
                                                                          className="mb-0"
                                                                          size="sm"
                                                                        >
                                                                          <Form.Control
                                                                            type="text"
                                                                            value={
                                                                              section.name
                                                                            }
                                                                            onChange={(
                                                                              e
                                                                            ) => {
                                                                              onSectionUpdate(
                                                                                e
                                                                                  .target
                                                                                  .value,
                                                                                section.id,
                                                                                module.id
                                                                              );
                                                                            }}
                                                                            onBlur={(
                                                                              e
                                                                            ) => {
                                                                              onSectionChange(
                                                                                section
                                                                              );
                                                                            }}
                                                                          />
                                                                          <DeleteButton
                                                                            onClick={() =>
                                                                              onSectionDelete(
                                                                                section,
                                                                                module.id
                                                                              )
                                                                            }
                                                                          />
                                                                        </InputGroup>
                                                                      </div>
                                                                    </Accordion.Header>
                                                                    <Accordion.Body>
                                                                      <Row>
                                                                        <Col
                                                                          xs
                                                                          md={{
                                                                            span: 10,
                                                                            offset: 1,
                                                                          }}
                                                                        >
                                                                          {(
                                                                            section.subsections ||
                                                                            []
                                                                          )
                                                                            .length >
                                                                            0 && (
                                                                            <Droppable
                                                                              droppableId={section.id.toString()}
                                                                              type="SUBSECTION"
                                                                            >
                                                                              {(
                                                                                provided
                                                                              ) => (
                                                                                <div
                                                                                  {...provided.droppableProps}
                                                                                  ref={
                                                                                    provided.innerRef
                                                                                  }
                                                                                >
                                                                                  {(
                                                                                    section.subsections ||
                                                                                    []
                                                                                  ).map(
                                                                                    (
                                                                                      subsection,
                                                                                      subsectionIndex
                                                                                    ) => (
                                                                                      <Draggable
                                                                                        key={
                                                                                          subsection.id
                                                                                        }
                                                                                        draggableId={`subsection-${subsection.id}`}
                                                                                        index={
                                                                                          subsectionIndex
                                                                                        }
                                                                                      >
                                                                                        {(
                                                                                          provided,
                                                                                          snapshot
                                                                                        ) => (
                                                                                          <div
                                                                                            ref={
                                                                                              provided.innerRef
                                                                                            }
                                                                                            {...provided.draggableProps}
                                                                                            style={{
                                                                                              ...provided
                                                                                                .draggableProps
                                                                                                .style,
                                                                                              transition:
                                                                                                "all 0.3s ease",
                                                                                              backgroundColor:
                                                                                                snapshot.isDragging
                                                                                                  ? "#f8f9fa"
                                                                                                  : "white",
                                                                                              marginBottom:
                                                                                                "10px",
                                                                                            }}
                                                                                          >
                                                                                            <InputGroup
                                                                                              className="mb-3"
                                                                                              size="sm"
                                                                                            >
                                                                                              <InputGroup.Text
                                                                                                {...provided.dragHandleProps}
                                                                                                style={{
                                                                                                  cursor:
                                                                                                    snapshot.isDragging
                                                                                                      ? "grabbing"
                                                                                                      : "grab",
                                                                                                }}
                                                                                              >
                                                                                                <Icon.GripVertical />
                                                                                              </InputGroup.Text>
                                                                                              <InputGroup.Text>
                                                                                                <span className="badge bg-dark">
                                                                                                  {
                                                                                                    subsection.position
                                                                                                  }
                                                                                                </span>
                                                                                              </InputGroup.Text>
                                                                                              <Form.Control
                                                                                                type="text"
                                                                                                value={
                                                                                                  subsection.name
                                                                                                }
                                                                                                onChange={(
                                                                                                  e
                                                                                                ) => {
                                                                                                  onSubsectionUpdate(
                                                                                                    e
                                                                                                      .target
                                                                                                      .value,
                                                                                                    subsection.id,
                                                                                                    section.id,
                                                                                                    module.id
                                                                                                  );
                                                                                                }}
                                                                                                onBlur={(
                                                                                                  e
                                                                                                ) => {
                                                                                                  onSubsectionChange(
                                                                                                    subsection
                                                                                                  );
                                                                                                }}
                                                                                              />
                                                                                              <Stack
                                                                                                direction="horizontal"
                                                                                                gap={
                                                                                                  1
                                                                                                }
                                                                                              >
                                                                                                <Button
                                                                                                  variant="primary"
                                                                                                  size="sm"
                                                                                                  onClick={() =>
                                                                                                    handleShowDetails(
                                                                                                      subsection
                                                                                                    )
                                                                                                  }
                                                                                                >
                                                                                                  <a>
                                                                                                    <FontAwesomeIcon
                                                                                                      icon={
                                                                                                        faList
                                                                                                      }
                                                                                                      className="text-white"
                                                                                                      style={{
                                                                                                        paddingRight:
                                                                                                          "3px",
                                                                                                      }}
                                                                                                    />
                                                                                                    Detalles
                                                                                                  </a>
                                                                                                </Button>
                                                                                                <Button
                                                                                                  variant="primary"
                                                                                                  size="sm"
                                                                                                  onClick={() =>
                                                                                                    handleStartMove(
                                                                                                      subsection,
                                                                                                      section.id,
                                                                                                      module.id
                                                                                                    )
                                                                                                  }
                                                                                                >
                                                                                                  <a>
                                                                                                    <FontAwesomeIcon
                                                                                                      icon={
                                                                                                        faArrowRight
                                                                                                      }
                                                                                                      className="text-white"
                                                                                                      style={{
                                                                                                        paddingRight:
                                                                                                          "3px",
                                                                                                      }}
                                                                                                    />
                                                                                                    Mover
                                                                                                  </a>
                                                                                                </Button>
                                                                                                <DeleteButton
                                                                                                  onClick={() =>
                                                                                                    onSubsectionDelete(
                                                                                                      subsection,
                                                                                                      section.id,
                                                                                                      module.id
                                                                                                    )
                                                                                                  }
                                                                                                />
                                                                                              </Stack>
                                                                                            </InputGroup>
                                                                                          </div>
                                                                                        )}
                                                                                      </Draggable>
                                                                                    )
                                                                                  )}
                                                                                  {
                                                                                    provided.placeholder
                                                                                  }
                                                                                </div>
                                                                              )}
                                                                            </Droppable>
                                                                          )}
                                                                          <hr />
                                                                          <Row>
                                                                            <Col>
                                                                              <Button
                                                                                variant="primary"
                                                                                onClick={() =>
                                                                                  onSubSectionInsert(
                                                                                    section,
                                                                                    module.id
                                                                                  )
                                                                                }
                                                                                size="sm"
                                                                              >
                                                                                <a>
                                                                                  <FontAwesomeIcon
                                                                                    icon={
                                                                                      faAdd
                                                                                    }
                                                                                    className="text-white"
                                                                                    style={{
                                                                                      paddingRight:
                                                                                        "3px",
                                                                                    }}
                                                                                  />
                                                                                  Nueva
                                                                                  subsección
                                                                                </a>
                                                                              </Button>
                                                                            </Col>
                                                                          </Row>
                                                                        </Col>
                                                                      </Row>
                                                                    </Accordion.Body>
                                                                  </Accordion.Item>
                                                                </div>
                                                              )}
                                                            </Draggable>
                                                          )
                                                        )}
                                                        {provided.placeholder}
                                                      </Accordion>
                                                    </div>
                                                  )}
                                                </Droppable>
                                              )}
                                              <hr />
                                              <Row>
                                                <Col>
                                                  <Button
                                                    variant="primary"
                                                    onClick={() =>
                                                      onSectionInsert(module)
                                                    }
                                                    size="sm"
                                                  >
                                                    <a>
                                                      <FontAwesomeIcon
                                                        icon={faAdd}
                                                        className="text-white"
                                                        style={{
                                                          paddingRight: "3px",
                                                        }}
                                                      />
                                                      Nueva sección
                                                    </a>
                                                  </Button>
                                                </Col>
                                              </Row>
                                            </Col>
                                          </Row>
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </Accordion>
                          </div>
                        )}
                      </Droppable>
                    )}
                  </Col>
                </Row>
                <Row className="pt-3">
                  <Col>
                    {policyTypeId > -1 && (
                      <div className="d-flex gap-2">
                        <Button variant="primary" onClick={onModuleInsert}>
                          <a>
                            <FontAwesomeIcon
                              icon={faAdd}
                              className="text-white"
                              style={{
                                paddingRight: "3px",
                              }}
                            />
                            Nuevo módulo
                          </a>
                        </Button>
                        <Button variant="primary" onClick={handleCloneClick}>
                          <a>
                            <FontAwesomeIcon
                              icon={faCopy}
                              className="text-white"
                              style={{
                                paddingRight: "3px",
                              }}
                            />
                            Clonar auditoría
                          </a>
                        </Button>
                      </div>
                    )}
                  </Col>
                </Row>
              </Container>
            </Card.Body>
          </Card>
        </DragDropContext>
      </Container>

      <Modal show={showCloneModal} onHide={handleCloseCloneModal}>
        <Modal.Header closeButton>
          <Modal.Title>Clonar auditoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloneError && <Alert variant="danger">{cloneError}</Alert>}
          <Form.Group>
            <Form.Label>Nuevo nombre de la auditoría:</Form.Label>
            <Form.Control
              type="text"
              placeholder="Ingrese el nuevo nombre..."
              value={newPolicyName}
              onChange={(e) => setNewPolicyName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCloneModal}>
            <a>
              <FontAwesomeIcon
                icon={faBan}
                style={{
                  paddingRight: "3px",
                }}
              />
              Cancelar
            </a>
          </Button>
          <Button variant="primary" onClick={handleCloneSave}>
            <a>
              <FontAwesomeIcon
                icon={faCopy}
                className="text-white"
                style={{
                  paddingRight: "3px",
                }}
              />
              Clonar
            </a>
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedSubSection && (
        <Modal
          show={showDetails}
          fullscreen={true}
          onHide={() => setShowDetails(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Preguntas</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AuditQuestionsManager
              subsection={selectedSubSection}
            ></AuditQuestionsManager>
          </Modal.Body>
        </Modal>
      )}

      <Modal
        show={showMoveModal}
        onHide={handleCloseMoveModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Mover Subsección</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Mover la subsección:{" "}
            <strong>{subsectionToMove?.subsection.name}</strong>
          </p>
          <p>Selecciona la nueva sección de destino:</p>
          <ListGroup style={{ maxHeight: "400px", overflowY: "auto" }}>
            {modules.map((module) => (
              <React.Fragment key={`module-group-${module.id}`}>
                <ListGroup.Item variant="dark" className="fw-bold">
                  {module.name}
                </ListGroup.Item>
                {(module.sections || [])
                  .filter((s) => s.id !== subsectionToMove?.fromSectionId)
                  .map((section) => (
                    <ListGroup.Item
                      key={section.id}
                      action
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <span className="badge bg-secondary me-2">
                          {section.position}
                        </span>
                        {section.name}
                      </span>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleConfirmMove(section.id)}
                      >
                        <a>
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            style={{
                              paddingRight: "3px",
                            }}
                          />
                          Mover aquí
                        </a>
                      </Button>
                    </ListGroup.Item>
                  ))}
              </React.Fragment>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMoveModal}>
            <a>
              <FontAwesomeIcon
                icon={faBan}
                style={{
                  paddingRight: "3px",
                }}
              />
              Cancelar
            </a>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
